import { useCallback } from 'react';
import { SelectInputProps, TextInputProps } from '@razorpay/blade/components';
import { OfficeLocationFormData } from '../types';
import { useOfficeLocationFormState } from './useOfficeLocationFormState';

type FieldType = 'text' | 'select';

type RegisterTextFieldReturnType = Pick<
  TextInputProps,
  'name' | 'onChange' | 'errorText' | 'validationState' | 'value' | 'isDisabled' | 'onFocus'
>;

type RegisterSelectReturnType = Pick<
  SelectInputProps,
  'name' | 'onChange' | 'errorText' | 'validationState' | 'value' | 'isDisabled' | 'onFocus'
>;

export type OfficeLocationRegister = <K extends FieldType = 'text'>(props: {
  name: keyof OfficeLocationFormData;
  type?: K;
  onSuccess?: () => void;
}) => K extends 'text'
  ? RegisterTextFieldReturnType
  : K extends 'select'
  ? RegisterSelectReturnType
  : RegisterTextFieldReturnType;

export const useOfficeLocationRegister = (
  formId: string | number,
  context: ReturnType<typeof useOfficeLocationFormState>,
): OfficeLocationRegister => {
  const { formData, formErrors, isSubmitting, updateField, clearError } = context;

  const register: OfficeLocationRegister = useCallback(
    ({ name, type = 'text', onSuccess }) => {
      const fieldError = formErrors[name];
      const value = formData[name];

      const onChange = (props: any) => {
        const newValue = type === 'select' ? props.values : props.value;
        updateField(name, newValue);
        onSuccess?.();
      };

      const onFocus = () => clearError(name);

      if (type === 'select') {
        return {
          name,
          errorText: fieldError,
          validationState: fieldError ? 'error' : 'none',
          value: value ?? [],
          isDisabled: isSubmitting,
          onChange,
          onFocus,
        } as RegisterSelectReturnType as any;
      }

      return {
        name,
        errorText: fieldError,
        validationState: fieldError ? 'error' : 'none',
        value: value ?? '',
        isDisabled: isSubmitting,
        onChange,
        onFocus,
      } as RegisterTextFieldReturnType as any;
    },
    [formData, formErrors, isSubmitting, updateField, clearError],
  );

  return register;
};
